import classNames from 'classnames'
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import styled from 'styled-components'
import _ from 'lodash'

import * as Icon from 'react-feather'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { format } from 'date-fns'
import { enUS, ptBR } from 'date-fns/locale'
import { Show } from '~/components/commons'

const EditCategoryCard = ({ category }) => {
  const googleServices = React.useMemo(() => {
    if (!category?.serviceTypes?.length) return []

    return category.serviceTypes
  }, [category])

  return (
    <>
      <Show condition={googleServices?.length}>
        <h6>
          <FormattedMessage id="Services" />
        </h6>
        <ul>
          {googleServices.map(service => (
            <li key={service.serviceTypeId}>{service.displayName}</li>
          ))}
        </ul>
      </Show>
      <Show condition={!googleServices?.length}>
        <FormattedMessage id="No services were found for this category" />
      </Show>
    </>
  )
}

const LocationPreviewModal = ({
  isOpen,
  toggle = () => {},
  initialData = {},
  data = {},
  editedFields = {},
  isValid,
  handleUpdate = () => {},
}) => {
  const locationEdited = Object.values(editedFields?.location).some(
    field => field
  )
  const hoursEdited = Object.values(editedFields?.hours).some(field => field)
  const categoriesEdited = Object.values(editedFields?.categories).some(
    field => field
  )
  const [activeTab, setActiveTab] = useState(
    locationEdited ? '1' : hoursEdited ? '2' : categoriesEdited ? '3' : '1'
  )
  const [collapsedItem, setCollapsedItem] = useState({
    id: '',
    status: 'Closed',
  })
  const intl = useIntl()

  const formatAddress = address => {
    let result = ''
    if (!address) return result

    if (address.addressLines[0]) {
      result += address.addressLines[0]
    }

    if (address.addressLines[1]) {
      result += address.addressLines[1]
    }

    if (address.sublocality) {
      result += `, ${address.sublocality}`
    }

    if (address.locality) {
      result += ` - ${address.locality}`
    }

    if (address.administrativeArea) {
      result += ` - ${address.administrativeArea}`
    }

    if (address.postalCode) {
      result += `, ${address.postalCode}`
    }

    return result
  }

  const categories = useMemo(() => {
    const result = []
    if (!data?.categories) return result

    const primary = data?.categories?.primaryCategory
    const additional = data?.categories?.additionalCategories

    if (primary) {
      result.push(primary.displayName)
    }

    if (additional) {
      const categoryNames = additional.map(category => category.displayName)
      result.push(...categoryNames)
    }

    return result
  }, [data])

  const collapseItems = useMemo(() => {
    const result = []
    if (data?.categories?.primaryCategory?.name) {
      setCollapsedItem({ id: data?.categories?.primaryCategory?.name })
      result.push(data?.categories?.primaryCategory)
    }
    if (data?.categories?.additionalCategories) {
      result.push(...data.categories.additionalCategories)
    }

    return result.map(category => ({
      id: category.name,
      title: category.displayName,
      category,
      content: <EditCategoryCard category={category} />,
    }))
  }, [data])

  const getRegularHourDescription = useCallback(
    (days = []) => {
      const formatTime = time => String(time).padStart(2, '0')
      const formattedDays = days.map(day => {
        let description = ''
        if (!day.closeTime || !day.openTime) return null

        const { hours: openHours, minutes: openMinutes = '00' } = day.openTime
        description += `${formatTime(openHours)}:${formatTime(openMinutes)}-`

        const {
          hours: closeHours,
          minutes: closeMinutes = '00',
        } = day.closeTime
        description += `${formatTime(closeHours)}:${formatTime(closeMinutes)}`

        return description
      })

      if (!formattedDays.filter(Boolean).length) {
        return [intl.formatMessage({ id: 'Closed' })]
      }

      return formattedDays.filter(Boolean)
    },
    [intl]
  )

  const formattedDateFromGoogle = useCallback(
    period => {
      if (!period.startDate) return null
      const { day, month, year } = period.startDate

      const date = new Date(year, +month - 1, day)
      return format(date, 'P', {
        locale: intl.locale === 'br' ? ptBR : enUS,
      })
    },
    [intl.locale]
  )

  const regularHours = useMemo(() => {
    const regularPeriods = data?.regularHours?.periods
    if (!regularPeriods.length) return []
    const regularHoursByWeekDay = _.groupBy(regularPeriods, 'openDay')

    const hours = Object.entries(regularHoursByWeekDay).reduce(
      (acc, [day, values]) => {
        if (!values.length) {
          acc.push({
            title: intl.formatMessage({ id: day }),
            period: [intl.formatMessage({ id: 'Closed' })],
          })
        } else {
          acc.push({
            title: intl.formatMessage({ id: day }),
            period: getRegularHourDescription(values),
          })
        }
        return acc
      },
      []
    )

    return hours
  }, [data, intl, getRegularHourDescription])

  const specialHours = useMemo(() => {
    const specialHoursPeriods = data?.specialHours?.specialHourPeriods
    if (!specialHoursPeriods.length) return []

    const specialHourPeriodsDays = _.groupBy(
      specialHoursPeriods,
      formattedDateFromGoogle
    )

    const days = Object.entries(specialHourPeriodsDays).reduce(
      (acc, [day, values]) => {
        if (!values.length) {
          acc.push({
            title: day,
            period: [intl.formatMessage({ id: 'Closed' })],
          })
        } else {
          acc.push({
            title: day,
            period: getRegularHourDescription(values),
          })
        }
        return acc
      },
      []
    )

    return days
  }, [data, getRegularHourDescription, intl, formattedDateFromGoogle])

  const toggleCollapse = useCallback(
    collapseID => {
      setCollapsedItem(prevState => ({
        id: prevState.id !== collapseID ? collapseID : '',
      }))
    },
    [setCollapsedItem]
  )

  const toggleStatus = useCallback(
    (id, status) => {
      if (id === collapsedItem.id) {
        setCollapsedItem({ id, status })
      }
    },
    [setCollapsedItem, collapsedItem]
  )

  useEffect(() => {
    setActiveTab(
      locationEdited ? '1' : hoursEdited ? '2' : categoriesEdited ? '3' : '1'
    )
  }, [locationEdited, hoursEdited, categoriesEdited])

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader>
        <FormattedMessage id="Location Update" />
        <div>
          {initialData?.title}{' '}
          {initialData?.storeCode && `(${initialData.storeCode})`}
        </div>
        <div>{formatAddress(initialData?.storefrontAddress)}</div>
      </ModalHeader>
      <ModalBody>
        <Nav tabs className="nav-top mt-1 box-shadow-0">
          {locationEdited && (
            <NavItem>
              <NavLinkPane
                className={classNames('mr-1', {
                  active: activeTab === '1',
                })}
                onClick={() => setActiveTab('1')}
              >
                <Icon.MapPin className="mr-1" />
                <FormattedMessage id="General" />
              </NavLinkPane>
            </NavItem>
          )}
          {hoursEdited && (
            <NavItem>
              <NavLinkPane
                className={classNames('mr-1', {
                  active: activeTab === '2',
                })}
                onClick={() => setActiveTab('2')}
              >
                <Icon.Clock className="mr-1" />
                <FormattedMessage id="Horary" />
              </NavLinkPane>
            </NavItem>
          )}
          {categoriesEdited && (
            <NavItem>
              <NavLinkPane
                className={classNames('mr-1', {
                  active: activeTab === '3',
                })}
                onClick={() => setActiveTab('3')}
              >
                <Icon.Briefcase className="mr-1" />
                <FormattedMessage id="Services" />
              </NavLinkPane>
            </NavItem>
          )}
        </Nav>
        <TabContent activeTab={activeTab} className="mt-1 w-100">
          {locationEdited && (
            <TabPane tabId="1" className="bg-transparent p-1 m--1">
              {editedFields?.location?.titleSwitch && (
                <>
                  <label>{intl.formatMessage({ id: 'Name' })}:</label>
                  <h2>{data?.title}</h2>
                </>
              )}

              {editedFields?.location?.storeCodeSwitch && (
                <>
                  <label>{intl.formatMessage({ id: 'Location Code' })}:</label>
                  <p>{data?.storeCode}</p>
                </>
              )}

              {editedFields?.location?.storefrontAdressSwitch && (
                <>
                  <label>{intl.formatMessage({ id: 'Address' })}:</label>
                  <p className="mb-50">
                    {formatAddress(data?.storefrontAddress)}
                  </p>
                </>
              )}

              {editedFields?.location?.['openInfo.statusSwitch'] && (
                <>
                  <label>
                    {intl.formatMessage({ id: 'Location Status' })}:
                  </label>
                  <p className="w-25 d-block mb-50">
                    <FormattedMessage id={data?.openInfo?.status} />
                  </p>
                </>
              )}

              {editedFields?.location?.['openInfo.openingDateSwitch'] && (
                <>
                  <label>{intl.formatMessage({ id: 'Opening Date' })}:</label>
                  <p className="w-25 d-block mb-50">
                    {data?.openInfo?.openingDate &&
                      `${data?.openInfo?.openingDate.getDate()}/${
                        data?.openInfo?.openingDate.getMonth() + 1
                      }/${data?.openInfo?.openingDate.getFullYear()}`}
                  </p>
                </>
              )}

              {editedFields?.location?.['phoneNumbers.primaryPhoneSwitch'] && (
                <>
                  <label>{intl.formatMessage({ id: 'Phone' })}:</label>
                  <div className="d-flex">
                    {data?.phoneNumbers?.primaryPhone && (
                      <>
                        <span className="mr-50">
                          <b>{intl.formatMessage({ id: 'Main' })}:</b>
                        </span>
                        {data?.phoneNumbers?.primaryPhone}
                      </>
                    )}
                    {data?.phoneNumbers?.additionalPhones?.length && (
                      <>
                        <span className="mr-50 ml-1">
                          <b>{intl.formatMessage({ id: 'Additional' })}:</b>
                        </span>
                        {data?.phoneNumbers?.additionalPhones?.map(
                          (phone, index, arr) => (
                            <span key={phone}>
                              {phone}
                              {index === arr.length - 1 ? '' : ','}
                            </span>
                          )
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              {editedFields?.location?.[
                'adWordsLocationExtensions.adPhoneSwitch'
              ] && (
                <>
                  <label>
                    {intl.formatMessage({ id: 'Google Ads Number' })}:
                  </label>
                  <p className="w-25 d-block mb-50">
                    {data?.adWordsLocationExtensions?.adPhone}
                  </p>
                </>
              )}

              {editedFields?.location?.websiteUriSwitch && (
                <>
                  <label>{intl.formatMessage({ id: 'Website' })}:</label>
                  <div>
                    <a
                      href={data?.websiteUri || '#'}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {decodeURI(data?.websiteUri)}
                    </a>
                  </div>
                </>
              )}

              {editedFields?.location?.['profile.descriptionSwitch'] && (
                <>
                  <label>{intl.formatMessage({ id: 'Description' })}:</label>
                  <div>{data?.profile?.description}</div>
                </>
              )}

              {editedFields?.location?.['categories.primaryCategorySwitch'] && (
                <>
                  <label>{intl.formatMessage({ id: 'Categories' })}:</label>
                  <ul>
                    {categories?.map(category => (
                      <li key={category}>{category}</li>
                    ))}
                  </ul>
                </>
              )}

              {editedFields?.location?.labelsSwitch && (
                <>
                  <label>{intl.formatMessage({ id: 'Labels' })}:</label>
                  <div className="d-flex">
                    {categories?.map(category => (
                      <Badge key={category} color="primary" className="mr-1">
                        {category}
                      </Badge>
                    ))}
                  </div>
                </>
              )}
            </TabPane>
          )}
          {hoursEdited && (
            <TabPane tabId="2" className="bg-transparent p-1 m--1">
              {editedFields?.hours?.openHoursFields && (
                <>
                  <h4 className="mb-1">
                    <FormattedMessage id="Regular hours" />
                  </h4>

                  <div>
                    {regularHours.map(({ title, period = [] }, i) => (
                      <div key={`regular-hour-${i}`} className="d-flex">
                        <p className="mb-25 mr-1">
                          <b>{title}</b>
                        </p>
                        {period.map((period, i) => (
                          <p key={`period-${i}`} className="mr-50">
                            {period}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {editedFields?.hours?.specialHoursFields && (
                <>
                  <h4 className="my-1">
                    <FormattedMessage id="Special hours" />
                  </h4>

                  <div>
                    {specialHours.map(({ title, period = [] }, i) => (
                      <div key={`special-hour-${i}`} className="d-flex">
                        <p className="mb-25 mr-1">
                          <b>{title}</b>
                        </p>
                        {period.map((period, i) => (
                          <p key={`special-period-${i}`} className="mr-50">
                            {period}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </TabPane>
          )}
          {categoriesEdited && (
            <TabPane tabId="3" className="bg-transparent p-1 m--1">
              {collapseItems
                .filter(
                  collapseItem => editedFields?.categories?.[collapseItem.id]
                )
                .map((collapseItem, i) => (
                  <Card
                    key={collapseItem.id}
                    className={classNames('px-2 no-shadow', {
                      'collapse-collapsed':
                        collapsedItem.status === 'Closed' &&
                        collapsedItem.id === collapseItem.id,
                      'collapse-shown':
                        collapsedItem.status === 'Opened' &&
                        collapsedItem.id === collapseItem.id,
                      closing:
                        collapsedItem.status === 'Closing...' &&
                        collapsedItem.id === collapseItem.id,
                      opening:
                        collapsedItem.status === 'Opening...' &&
                        collapsedItem.id === collapseItem.id,
                    })}
                  >
                    <CardHeader
                      className="px-0"
                      onClick={() => toggleCollapse(collapseItem.id)}
                    >
                      <CardTitle className="lead collapse-title collapsed">
                        {collapseItem.title}
                        <CategorySubtitle>
                          {i === 0 ? (
                            <FormattedMessage id="Primary category" />
                          ) : (
                            <FormattedMessage id="Additional category" />
                          )}
                        </CategorySubtitle>
                      </CardTitle>
                      <Icon.ChevronDown size={15} className="collapse-icon" />
                    </CardHeader>
                    <Collapse
                      isOpen={collapseItem.id === collapsedItem.id}
                      onEntering={() => toggleStatus(collapseItem.id, 'Opened')}
                      onEntered={() =>
                        toggleStatus(collapseItem.id, 'Opening...')
                      }
                      onExiting={() => toggleStatus(collapseItem.id, 'Closed')}
                      onExited={() =>
                        toggleStatus(collapseItem.id, 'Closing...')
                      }
                    >
                      <CardBody className="p-0 cursor-default">
                        {collapseItem.content}
                      </CardBody>
                    </Collapse>
                  </Card>
                ))}
            </TabPane>
          )}
        </TabContent>
      </ModalBody>
      <ModalFooter>
        <Button outline color="primary" onClick={toggle} className="mr-1">
          <FormattedMessage id="Edit" />
        </Button>
        <Button disabled={!isValid} onClick={handleUpdate} color="success">
          <FormattedMessage id="Update now" />
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const NavLinkPane = styled(NavLink)`
  color: #626262 !important;

  &.active {
    font-weight: 600;
    color: #7367f0;
    &::after {
      background-color: red !important;
      transform: rotate(0deg) !important;
      left: 0px !important;
      width: 100% !important;
      bottom: 1px !important;
      top: auto !important;
    }
  }
`

const CategorySubtitle = styled.p`
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  color: #8d8d8d;
`
export default LocationPreviewModal
